import { z } from 'zod'
import {
  greetingCardDecorationTransformSchema,
  greetingCardInsideFontSourceSchema,
  greetingCardPhotoPlacementSchema,
  greetingCardSessionAssetReferenceSchema,
} from './builder-payload'
import { greetingCardRectSchema } from './schemas'

export const GREETING_CARD_PRINT_MANIFEST_TYPE = 'giftie_greeting_card_print_manifest'
export const GREETING_CARD_PRINT_MANIFEST_VERSION = 1 as const
export const GREETING_CARD_PRINT_MANIFEST_PAYLOAD_KEY = '_giftiePrintManifest'

export const greetingCardPrintThemeSchema = z.object({
  accentColor: z.string().min(1),
  blockColor: z.string().min(1),
  textOnBlockColor: z.string().min(1),
  brandFontFamily: z.string().nullable(),
  brandFontUrl: z.string().nullable(),
  brandFontWeight: z.string().nullable(),
  brandFontFormat: z.string().nullable(),
})

const greetingCardPrintDecorationSchema = z.object({
  id: z.string().nullable(),
  side: z.enum(['outside', 'inside']),
  transform: greetingCardDecorationTransformSchema,
  asset: z.object({
    source: z.string().nullable(),
    mimeType: z.string().nullable(),
    width: z.number().nullable(),
    height: z.number().nullable(),
    emoji: z.string().nullable(),
    qrTargetUrl: z.string().nullable(),
    qrLabel: z.string().nullable(),
    qrColorHex: z.string().nullable().optional(),
    url: z.string().nullable(),
    storage: greetingCardSessionAssetReferenceSchema.nullable(),
  }),
})

const greetingCardPrintFrontTextLayerSchema = z.object({
  id: z.string().min(1),
  label: z.string().nullable(),
  text: z.string(),
  bounds: greetingCardRectSchema.nullable(),
  required: z.boolean().nullable(),
  maxLength: z.number().nullable(),
  defaultValue: z.string().nullable(),
  defaultFontSize: z.number().nullable(),
  resolvedFontSizeRatio: z.number().nullable(),
  fontFamily: z.string().nullable(),
  color: z.string().nullable(),
  textAlign: z.string().nullable(),
  fontWeight: z.string().nullable(),
})

export const greetingCardPrintLogoSlotSchema = z.object({
  bounds: greetingCardRectSchema,
  objectFit: z.string().optional(),
}).passthrough()

export const greetingCardPrintManifestSchema = z.object({
  manifestType: z.literal(GREETING_CARD_PRINT_MANIFEST_TYPE),
  manifestVersion: z.literal(GREETING_CARD_PRINT_MANIFEST_VERSION),
  generatedAt: z.string().datetime(),
  session: z.object({
    id: z.string().uuid(),
    shop: z.string().min(1),
  }),
  card: z.object({
    designId: z.string().min(1),
    designName: z.string().min(1),
    variantId: z.string().min(1),
    shopifyVariantId: z.string().min(1),
    orientation: z.enum(['portrait', 'landscape']),
    cardFormat: z.enum(['folded', 'flat']),
    paperSize: z.enum(['A5', 'A6', '4x6', '5x7']),
    supportedPaperSizes: z.array(z.enum(['A5', 'A6', '4x6', '5x7'])),
    frontImageUrl: z.string().url().nullable(),
    thumbnailUrl: z.string().url().nullable(),
  }),
  theme: greetingCardPrintThemeSchema,
  paper: z.object({
    widthMm: z.number(),
    heightMm: z.number(),
    widthIn: z.number(),
    heightIn: z.number(),
    aspectRatio: z.string().min(1),
    templatePx: z.object({ width: z.number(), height: z.number() }),
    safeAreaPx: greetingCardRectSchema,
    printBorderPercentages: z.object({
      horizontal: z.number(),
      vertical: z.number(),
    }),
  }),
  front: z.object({
    type: z.enum(['static', 'photo']),
    textFontPolicy: z.string().nullable(),
    preview: z.object({
      frontUrl: z.string().url().nullable(),
      thumbUrl: z.string().url().nullable(),
    }),
    printAsset: greetingCardSessionAssetReferenceSchema.nullable().optional(),
    background: z.unknown().nullable(),
    backdrop: z.unknown().nullable(),
    logoSlot: greetingCardPrintLogoSlotSchema.nullable(),
    textLayers: z.array(greetingCardPrintFrontTextLayerSchema),
    photo: z.object({
      slotBounds: greetingCardRectSchema,
      maskUrl: z.string().url().nullable(),
      asset: greetingCardSessionAssetReferenceSchema.nullable(),
      placement: greetingCardPhotoPlacementSchema.nullable(),
    }).nullable(),
    decorations: z.array(greetingCardPrintDecorationSchema),
  }),
  inside: z.object({
    defaultMessage: z.string().nullable(),
    text: z.string(),
    typography: z.object({
      fontFamily: z.string().nullable(),
      fontSizePt: z.number(),
      resolvedFontSizePt: z.number().nullable(),
      resolvedFontSizeRatio: z.number().nullable().optional(),
      fontSource: greetingCardInsideFontSourceSchema.nullable().optional(),
      colorHex: z.string().nullable(),
      align: z.string(),
      weight: z.string(),
      vAlign: z.string(),
      maxChars: z.number(),
    }),
    logoSlot: greetingCardPrintLogoSlotSchema.nullable(),
    textBox: greetingCardRectSchema,
    decorations: z.array(greetingCardPrintDecorationSchema),
  }),
  attachment: z.object({
    type: z.string(),
    code: z.string(),
    playbackUrl: z.string().url().nullable(),
  }).nullable(),
})

export type GreetingCardPrintManifest = z.infer<typeof greetingCardPrintManifestSchema>
export type GreetingCardPrintTheme = z.infer<typeof greetingCardPrintThemeSchema>
